import * as React from "react";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { commitMutation, graphql, type RecordSourceSelectorProxy } from "relay-runtime";

import { useEffectAfterMount } from "~/lib/hooks";
import { environment } from "~/models";
import { Input } from "~/ui/legacyFellowIndex";

import type { sharedUserSettingsMutation as MutationType } from "~/__generated__/sharedUserSettingsMutation.graphql";

import styles from "./shared.module.css";

const { useState } = React;

export function SettingsPageTitle({ children }: React.PropsWithChildren<{}>) {
	return <h1 className={styles.pageTitle}>{children}</h1>;
}

export function SettingsPageSubtitle({ children }: React.PropsWithChildren<{}>) {
	return <p className={styles.pageSubtitle}>{children}</p>;
}

export function SectionTitle({ children }: React.PropsWithChildren<{}>) {
	return <h2 className={styles.sectionTitle}>{children}</h2>;
}

export function TextFieldLabel({ children }: React.PropsWithChildren<{}>) {
	return <label className={styles.textFieldLabel}>{children}</label>;
}

export function TextFieldInput(props: React.ComponentPropsWithoutRef<typeof Input>) {
	const { className, ...rest } = props;
	return <Input className={cx(className, styles.textFieldInput)} {...rest} />;
}

export function TextFieldDetails({ children }: React.PropsWithChildren<{}>) {
	return <small className={styles.textFieldDetails}>{children}</small>;
}

export function Divider() {
	return <hr className={styles.divider} />;
}

export function Row({ children }: React.PropsWithChildren<{}>) {
	return <div className={styles.row}>{children}</div>;
}

export function Left({ children }: React.PropsWithChildren<{}>) {
	return <div className={styles.rowLeft}>{children}</div>;
}

Row.Left = Left;

export function Right({ children }: React.PropsWithChildren<{}>) {
	return <div className={styles.rowRight}>{children}</div>;
}

Row.Right = Right;

export function Label({ children }: React.PropsWithChildren<{}>) {
	return <label className={styles.rowLabel}>{children}</label>;
}

export function SubLabel({ children }: React.PropsWithChildren<{}>) {
	return <small className={styles.rowSublabel}>{children}</small>;
}

export function InfoBanner({ children }: React.PropsWithChildren<{}>) {
	return (
		<div className={styles.infoBanner}>
			<FontAwesomeIcon icon={faInfoCircle} className={styles.infoBannerIcon} />
			<div>{children}</div>
		</div>
	);
}

export function ErrorBanner({ children }: React.PropsWithChildren<{}>) {
	return (
		<div className={cx(styles.infoBanner, styles.errorBanner)}>
			<FontAwesomeIcon icon={faInfoCircle} className={cx(styles.infoBannerIcon, styles.errorBannerIcon)} />
			<div>{children}</div>
		</div>
	);
}

export const sharedUserSettingsMutation = graphql`
	mutation sharedUserSettingsMutation($input: UserSettingsMutationInput!) {
		updateSettings(input: $input) {
			errors {
				field
				message
			}
		}
	}
`;

type MutationInput = MutationType["variables"]["input"];

export type UseSettingUpdaterFunction<ValueKey extends keyof MutationInput> = (
	store: RecordSourceSelectorProxy<MutationType["response"]>,
	data: MutationType["response"] | undefined | null,
	updatedValue: MutationInput[ValueKey],
) => void;

/**
 * Works like useState, but when setting the value, it also fires a GraphQL mutation to
 * update the value on the back-end, and updates the value in the local Relay store, so
 * we don't have to query for the value on the mutation.
 *
 * Optionally can be debounced.
 *
 * @remarks
 * The store updating is useful because Relay doesn't let us easily change which fields
 * we query at runtime, so we would otherwise have to query every settings field every
 * time we run this mutation, and there are a lot of settings fields.
 */
export function useUserSetting<ValueKey extends keyof MutationInput>(
	/** The key of the setting in the updateSettings mutation input */
	key: ValueKey,
	/** The initial value of the setting */
	initialValue: MutationInput[ValueKey],
	{
		debounce = false,
		updater = undefined,
		onCompleted,
	}: {
		debounce?: boolean;
		/** How to update the value in the local Relay store.
		 *
		 * If a function, it's provided to commitMutation's updater parameter.
		 * If a string, it's a path to the field to update, e.g. "viewer.settings.theme", and the value is set directly.
		 * If true, the field is assumed to be at `viewer.settings.${key}` and the value is set directly.
		 */
		updater?: UseSettingUpdaterFunction<ValueKey> | string | true;
		onCompleted?: (mutationResult: MutationType["response"], newValue: MutationInput[ValueKey]) => void;
	},
): [valueProp: MutationInput[ValueKey], onChangeProp: (value: MutationInput[ValueKey]) => void] {
	const [value, setValue] = useState(initialValue);

	useEffectAfterMount(() => {
		if (value === initialValue) return undefined;

		function sendUpdate() {
			commitMutation<MutationType>(environment, {
				mutation: sharedUserSettingsMutation,
				variables: { input: { [key]: value } },
				onCompleted: data => {
					onCompleted?.(data, value);
				},
				updater: (store, data) => {
					if (!Array.isArray(value) && typeof value === "object") {
						if (typeof updater !== "function") {
							console.warn(
								`The graphql type for "${key}" is an object - use the updater function to update the store.`,
							);
							return;
						}

						updater?.(store, data, value);
					} else if (typeof updater === "string" || updater === true) {
						const path = updater === true ? ["viewer", "settings", key] : updater.split(".");
						let node = store.getRoot();
						for (let i = 0; i < path.length; i++) {
							const fieldName = path[i];
							if (i < path.length - 1) {
								node = node.getLinkedRecord(fieldName);
								if (!node) break;
							} else {
								node.setValue(value, fieldName);
							}
						}
					} else if (typeof updater === "function") {
						updater?.(store, data, value);
					}
				},
			});
		}

		if (!debounce) {
			sendUpdate();
			return undefined;
		}

		const timeout = window.setTimeout(sendUpdate, 400);
		return () => window.clearTimeout(timeout);
	}, [key, value, initialValue, debounce, updater, onCompleted]);
	return [value, setValue];
}
